import React, { useState, useContext, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate, Link } from "react-router-dom";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import api from "../api";
import * as googleNativo from "../platform/googleAuth";
import * as appleNativo from "../platform/appleAuth";
import { AuthContext } from "../context/AuthContext";
import TenantSelect from "./TenantSelect";
import { theme } from "../theme";
const logo = "/metria-logo.svg";

const Login = () => {
  const { login, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // Quem já está logado não precisa ver o formulário (ex.: ao voltar dos Tutoriais).
  // Se veio de sessao expirada, api.js salvou a rota anterior — volta pra la.
  useEffect(() => {
    if (isAuthenticated) {
      let destino = "/";
      try {
        const salvo = sessionStorage.getItem('post_login_redirect');
        if (salvo && !salvo.startsWith('/login') && !salvo.startsWith('/register')) {
          destino = salvo;
        }
        sessionStorage.removeItem('post_login_redirect');
      } catch { /* sessionStorage desativado: ignora */ }
      navigate(destino, { replace: true });
    }
  }, [isAuthenticated, navigate]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needsTenant, setNeedsTenant] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [pendingProvider, setPendingProvider] = useState("google");

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await api.post("/auth/login", { email, senha });
      const { access_token, refresh_token, is_admin, is_superadmin, tenant } = response.data;
      login(access_token, is_admin, refresh_token, tenant, is_superadmin);
    } catch (err) {
      setError(err.response?.data?.erro || "Email ou senha inválidos.");
    } finally {
      setLoading(false);
    }
  };

  // No app o botao do @react-oauth/google nao renderiza (origem https://localhost
  // nao autorizada, e o Google bloqueia OAuth em webview). O fluxo nativo devolve
  // o mesmo idToken, entao cai no mesmo handler e o backend nao muda.
  const entrarComGoogleNativo = async () => {
    setError(null);
    const r = await googleNativo.entrar();
    if (r.ok) { await handleGoogleSuccess({ credential: r.idToken }); return; }
    if (r.motivo === 'cancelado') return;          // fechou o seletor, sem alarde
    setError(r.motivo === 'sem-token'
      ? 'O Google não devolveu as credenciais. Tente entrar com e-mail e senha.'
      : 'Não consegui abrir o login do Google agora.');
  };

  // Diretriz 4.8: no iOS, quem oferece login de terceiro precisa oferecer
  // tambem uma alternativa equivalente. Convive com o Google, nao o substitui —
  // 38% das contas entram so por ele e nao tem senha.
  const entrarComApple = async () => {
    setError(null);
    const r = await appleNativo.entrar();
    if (r.motivo === 'cancelado') return;
    if (!r.ok) {
      // O detalhe tecnico so aparece enquanto o fluxo nao foi validado num
      // iPhone. Nao da para ler log do iOS daqui, entao a tela e o unico canal
      // de diagnostico — remover assim que o login com Apple estiver provado.
      const detalhe = appleNativo.detalheDoErro && appleNativo.detalheDoErro();
      setError(r.motivo === 'sem-token'
        ? 'A Apple não devolveu as credenciais. Tente entrar com e-mail e senha.'
        : 'Não consegui abrir o login da Apple agora.' + (detalhe ? ` [${String(detalhe).slice(0, 140)}]` : ''));
      return;
    }
    try {
      // O nome so chega na PRIMEIRA autorizacao; mandamos junto para o backend
      // gravar naquele instante, porque a Apple nunca mais envia.
      const response = await api.post("/auth/apple", {
        identity_token: r.idToken,
        display_name: r.nome || undefined,
      });
      if (response.data.needs_tenant) {
        setPendingCredential(r.idToken);
        setPendingProvider('apple');
        setNeedsTenant(true);
        return;
      }
      const { access_token, refresh_token, is_admin, is_superadmin, tenant } = response.data;
      login(access_token, is_admin, refresh_token, tenant, is_superadmin);
    } catch (e) {
      setError(e?.response?.data?.erro || "Falha no login com Apple.");
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    try {
      const response = await api.post("/auth/google", { credential: credentialResponse.credential });
      if (response.data.needs_tenant) {
        setPendingCredential(credentialResponse.credential);
        setPendingProvider("google");
        setNeedsTenant(true);
        return;
      }
      const { access_token, refresh_token, is_admin, is_superadmin, tenant } = response.data;
      login(access_token, is_admin, refresh_token, tenant, is_superadmin);
    } catch {
      setError("Falha no login com Google.");
    }
  };

  if (needsTenant && pendingCredential) return <TenantSelect credential={pendingCredential} provider={pendingProvider} />;

  return (
    <div style={styles.page}>
      {/* Decorative blobs */}
      <div style={{ ...styles.blob, top: '-120px', left: '-120px', background: 'radial-gradient(circle, rgba(232,180,160,0.5), transparent 70%)' }} />
      <div style={{ ...styles.blob, bottom: '-160px', right: '-100px', background: 'radial-gradient(circle, rgba(196,120,110,0.35), transparent 70%)' }} />

      <div style={styles.card}>
        <div style={styles.logoWrapper}>
          <img src={logo} alt="Metria" style={styles.logo} />
        </div>
        <p style={styles.subtitle}>Assistente para Confeiteiras</p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleEmailLogin} style={{ width: '100%' }}>
          <div style={styles.inputGroup}>
            <FaEnvelope style={styles.inputIcon} />
            <input
              type="email" placeholder="seu@email.com" value={email}
              onChange={e => setEmail(e.target.value)} required
              style={styles.input} autoComplete="email"
            />
          </div>
          <div style={styles.inputGroup}>
            <FaLock style={styles.inputIcon} />
            <input
              type={showPass ? "text" : "password"} placeholder="Sua senha" value={senha}
              onChange={e => setSenha(e.target.value)} required
              style={{ ...styles.input, paddingRight: 40 }} autoComplete="current-password"
            />
            <button type="button" onClick={() => setShowPass(!showPass)} style={styles.eyeBtn} tabIndex={-1}>
              {showPass ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <button type="submit" disabled={loading} style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Link to="/esqueci-senha" style={{ ...styles.link, fontSize: 13 }}>
              Esqueci minha senha
            </Link>
          </div>
        </form>

        <div style={styles.dividerText}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerLabel}>ou continue com</span>
          <span style={styles.dividerLine} />
        </div>

        <div style={styles.googleWrapper}>
          {googleNativo.disponivel() ? (
            <button
              type="button"
              onClick={entrarComGoogleNativo}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                width: '100%', minHeight: 48, borderRadius: 10, cursor: 'pointer',
                border: '1px solid #DADCE0', background: '#fff',
                fontSize: 15, fontWeight: 500, color: '#3C4043',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
              </svg>
              Entrar com Google
            </button>
          ) : (
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError("Login cancelado.")}
              theme="outline" size="large" width="320" text="signin_with"
              shape="rectangular" locale="pt-BR"
            />
          )}

          {/* So no iOS. A Apple exige destaque equivalente ao do outro botao,
              entao usa a mesma altura e largura — nao um link discreto. */}
          {appleNativo.disponivel() && (
            <button
              type="button"
              onClick={entrarComApple}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', minHeight: 48, borderRadius: 10,
                cursor: 'pointer', border: 'none', background: '#000',
                fontSize: 15, fontWeight: 500, color: '#fff',
              }}
            >
              <svg width="17" height="20" viewBox="0 0 17 20" aria-hidden="true" fill="#fff">
                <path d="M14.1 10.6c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.4s-2.2-.9-2.2-3.3z"/>
                <path d="M11.9 4.1c.6-.7 1-1.7.9-2.7-.9 0-2 .6-2.6 1.3-.6.6-1.1 1.7-.9 2.6 1 .1 2-.5 2.6-1.2z"/>
              </svg>
              Entrar com Apple
            </button>
          )}
        </div>

        <p style={styles.footerLink}>
          Não tem conta?{" "}
          <Link to="/register" style={styles.link}>Criar minha conta</Link>
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(155deg, #FDF7F4 0%, #F5E6E3 50%, #E8B4A0 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: theme.font, padding: '20px', position: 'relative', overflow: 'hidden',
  },
  blob: { position: 'absolute', width: 400, height: 400, borderRadius: '50%', pointerEvents: 'none', filter: 'blur(20px)' },
  card: {
    background: "rgba(255,255,255,0.98)", backdropFilter: "blur(20px)",
    borderRadius: 24, padding: "40px 36px", width: "100%", maxWidth: 400,
    textAlign: "center", boxShadow: "0 20px 60px rgba(61,44,44,0.15)",
    border: "1px solid rgba(255,255,255,0.6)", position: 'relative', zIndex: 1,
  },
  logoWrapper: { display: "flex", justifyContent: "center", marginBottom: 8 },
  logo: { width: 200, height: "auto", objectFit: "contain" },
  subtitle: {
    fontSize: 12, color: theme.colors.textSecondary, letterSpacing: 2.5,
    textTransform: "uppercase", margin: "0 0 32px 0", fontWeight: 500,
  },
  error: {
    color: theme.colors.danger, fontSize: 13, marginBottom: 16,
    background: theme.colors.dangerLight, borderRadius: 10,
    padding: '10px 14px', textAlign: 'left', border: `1px solid ${theme.colors.danger}30`,
  },
  inputGroup: { position: 'relative', marginBottom: 12 },
  inputIcon: {
    position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
    color: theme.colors.textMuted, fontSize: 14, pointerEvents: 'none',
  },
  input: {
    width: "100%", padding: "13px 14px 13px 42px", fontSize: 14,
    border: `1.5px solid ${theme.colors.border}`, borderRadius: 12,
    background: theme.colors.bgSection, color: theme.colors.text,
    outline: 'none', transition: 'border-color 0.2s, background 0.2s',
    boxSizing: 'border-box', fontFamily: theme.font,
  },
  eyeBtn: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: theme.colors.textMuted,
    cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center',
  },
  btnPrimary: {
    width: '100%', padding: '14px', fontSize: 15, fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
    color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer',
    marginTop: 6, boxShadow: '0 4px 14px rgba(196,120,110,0.3)',
    transition: 'transform 0.15s', fontFamily: theme.font,
  },
  dividerText: {
    display: 'flex', alignItems: 'center', gap: 12,
    margin: '24px 0 20px', color: theme.colors.textMuted, fontSize: 12,
  },
  dividerLine: { flex: 1, height: 1, background: theme.colors.border },
  dividerLabel: { whiteSpace: 'nowrap' },
  // Coluna, nao linha: com Google e Apple lado a lado os rotulos quebravam em
  // duas linhas e os botoes disputavam largura. Um por linha da a cada um a
  // mesma prominencia, que e o que a diretriz 4.8 pede.
  googleWrapper: {
    display: "flex", flexDirection: "column", gap: 10,
    alignItems: "stretch", marginBottom: 24,
  },
  footerLink: { fontSize: 13, color: theme.colors.textMuted, margin: 0 },
  link: { color: theme.colors.primary, cursor: "pointer", fontWeight: 600, textDecoration: 'none' },
};

export default Login;
